/**
 * Mock Client Utility Helpers
 * Network latency simulation and test failure injection.
 */

import { ApiEnvelope, ApiError } from '../../domain/types';
import { ClientConfig } from './types';

// Default config: 0ms in Node (no window), 120ms in the browser / jsdom so
// in-flight hydrations settle after user interactions in component tests.
export const DEFAULT_CONFIG: ClientConfig = {
  latencyMs: typeof window === 'undefined' ? 0 : 120,
  simulateNetworkError: false,
  simulateRateLimit: false,
};

export async function simulateNetwork<T>(
  fn: () => T,
  config: ClientConfig = DEFAULT_CONFIG,
): Promise<ApiEnvelope<T>> {
  if (config.latencyMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, config.latencyMs));
  }

  if (config.simulateRateLimit) {
    const error: ApiError = {
      code: 'RATE_LIMIT_EXCEEDED',
      message: 'Too Many Requests: Rate limit quota exceeded. Please retry later.',
      status: 429,
      retryAfter: 30,
    };
    throw error;
  }

  if (config.simulateNetworkError) {
    const error: ApiError = {
      code: 'SERVICE_UNAVAILABLE',
      message: 'Network Gateway Timeout: Unable to reach provider service.',
      status: 503,
    };
    throw error;
  }

  return {
    data: fn(),
    serverTime: new Date().toISOString(),
    rateLimit: {
      limit: 100,
      remaining: 94,
      resetAt: new Date(Date.now() + 3600000).toISOString(),
    },
  };
}

/**
 * Dev-only fixture selector: `?disconnected=jira,slack` makes those mock
 * providers report `not_configured` with empty lists, so the shared
 * connection panel can be exercised in the browser without touching secrets.
 * Never active under Vitest (deterministic unit tests) or without a window.
 */
export function isMockSourceDisconnected(source: string): boolean {
  return readMockSourceFlag('disconnected', source);
}

/**
 * Dev-only fixture selector: `?rejected=jira,github` makes those mock providers
 * report a rejected credential (401) with empty lists, so the credentials-
 * rejected state can be exercised in the browser without a real expired token.
 */
export function isMockSourceRejected(source: string): boolean {
  return readMockSourceFlag('rejected', source);
}

/** Provider wording for a mock 401, mirroring what the BFF forwards. */
export function mockRejectedCredentialMessage(source: string): string {
  if (source === 'jira')
    return 'Atlassian HTTP 401: Client must be authenticated to access this resource.';
  if (source === 'github') return 'GitHub GET /user → HTTP 401: Bad credentials';
  return 'Slack API error: invalid_auth';
}

function readMockSourceFlag(param: string, source: string): boolean {
  if (typeof window === 'undefined' || import.meta.env.MODE === 'test') return false;
  return new URLSearchParams(window.location.search)
    .getAll(param)
    .flatMap((value) => value.split(','))
    .map((name) => name.trim().toLowerCase())
    .includes(source);
}
